import { zodResolver } from '@hookform/resolvers/zod'
import {
  CalendarDays,
  Clock,
  Gauge,
  Globe,
  Hash,
  ListOrdered,
  Music2,
  Sparkles,
  Swords,
  TrendingUp,
  Trophy,
} from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/feedback/FormErrorSummary'
import { JobProgressPanel } from '@/components/feedback/JobProgressPanel'
import { BackToFactoryLink } from '@/features/factory/components/BackToFactoryLink'
import { GenerationTimeHint } from '@/features/factory/components/GenerationTimeHint'
import {
  QualityTierSelector,
  type QualityTier,
} from '@/features/factory/components/QualityTierSelector'
import { Button } from '@/components/ui/Button'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import {
  useFactoryCatalog,
  useStartFootball,
  type FootballInput,
} from '@/api/endpoints/factories'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import { cn } from '@/lib/cn'
import { LlmProviderPicker } from '../_shared/LlmProviderPicker'
import { videoFormatsFor } from '@/lib/videoFormats'

import { PresetBar } from '../_shared/PresetBar'
import { CinemaBackground } from '../ViralShortsForm/CinemaBackground'
import sharedStyles from '../ViralShortsForm/ViralShortsForm.module.scss'
import localStyles from './FootballForm.module.scss'

// Compose the shared form chrome with input styles tuned for single-line
// fields. `.input` / `.inputError` only live in the local module; everything
// else (shell, header, rows, etc.) comes from the shared module to stay
// visually consistent with the other factories.
const styles = { ...sharedStyles, ...localStyles }

const FIELD_LABELS: Record<string, string> = {
  season: 'Temporada',
  round: 'Rodada',
  language: 'Idioma',
  duration: 'Duração',
  music_mood: 'Trilha',
  video_type: 'Formato',
  theme: 'Recorte',
  show_standings_outro: 'Tabela no final',
}

// Issue #24 Feat #4 + card #103 — Segmented "Formato" maps to the three
// video_type slugs registered in the backend registry. ``top3_standings``
// is the historical default (last finished round); ``next_round_preview``
// previews upcoming fixtures (no scores); ``season_insight`` is a cut of
// the accumulated season and does not depend on a fresh round.
//
// Os rótulos saem de ``lib/videoFormats`` porque a lista de prioridade do
// agendador mostra os MESMOS nomes. Duplicar aqui faria as duas telas
// divergirem no dia em que uma fosse renomeada.
type MatchModeKey = 'top3_standings' | 'next_round_preview' | 'season_insight'
const MATCH_MODES: ReadonlyArray<SegmentedOption<MatchModeKey>> = videoFormatsFor('football').map(
  (f) => ({ value: f.value as MatchModeKey, label: f.label }),
)

// Cards #103/#104/#105 — espelha as chaves de ``season_insight/themes.py::THEMES``,
// na mesma ordem de prioridade editorial do backend. São 15 hoje.
//
// A lista é fixa aqui, como MATCH_MODES: o backend não publica os temas em
// nenhum endpoint hoje, e inventar um só para isso custaria mais que o
// risco de divergir. Se um slug sair de sincronia, o backend responde com
// ``KeyError`` no fetch — falha barulhenta, não vídeo errado.
//
// ``'auto'`` é sentinela de UI, não valor do backend: o Radix Select proíbe
// item com value vazio (string vazia é reservada para limpar a seleção), e
// o backend espera ``theme: ""`` para "escolha o primeiro disponível". A
// tradução acontece nas duas bordas — formToPayload e payloadToForm.
type ThemeKey =
  | 'auto'
  | 'streaks'
  | 'title_race'
  | 'scorer_pace'
  | 'home_away'
  | 'discipline'
  | 'fixture_difficulty'
  | 'blowouts'
  | 'geography'
  | 'attack_defense'
  | 'assists'
  | 'goalkeepers'
  | 'shooting'
  | 'passing'
  | 'fouls'
  | 'discipline_players'

const THEME_OPTIONS: ReadonlyArray<{ value: ThemeKey; label: string }> = [
  { value: 'auto', label: 'Automático (primeiro disponível)' },
  { value: 'streaks', label: 'Sequências — quem embala e quem afunda' },
  { value: 'title_race', label: 'Corrida do título — topo e as fronteiras' },
  { value: 'scorer_pace', label: 'Ritmo da artilharia — projeção em 38' },
  { value: 'home_away', label: 'Casa x fora — gigante e fantasma' },
  { value: 'discipline', label: 'Cartões — o mais sujo e o mais limpo' },
  { value: 'fixture_difficulty', label: 'Tabela pela frente — próximos 5' },
  { value: 'blowouts', label: 'Goleadas — maiores surras e jogos malucos' },
  { value: 'geography', label: 'Mapa — onde se joga e quem mais viaja' },
  { value: 'attack_defense', label: 'Ataque x defesa — os quatro extremos' },
  // Card #105 — rankings de jogador, na mesma ordem do dict THEMES.
  { value: 'assists', label: 'Assistências — quem mais serve gol' },
  { value: 'goalkeepers', label: 'Goleiros — quem mais defendeu' },
  { value: 'shooting', label: 'Pontaria — volume contra precisão' },
  { value: 'passing', label: 'Passes — o maestro da temporada' },
  { value: 'fouls', label: 'Faltas — o mais caçado e o mais faltoso' },
  { value: 'discipline_players', label: 'Cartões por jogador — os vilões' },
]

const THEME_KEYS = THEME_OPTIONS.map((o) => o.value)

const LANGUAGES: ReadonlyArray<SegmentedOption<'pt-br' | 'en' | 'both'>> = [
  { value: 'pt-br', label: 'PT-BR' },
  { value: 'en', label: 'EN' },
  { value: 'both', label: 'Ambos' },
]

type DurationKey = '30' | '40' | '60'
const DURATIONS: ReadonlyArray<SegmentedOption<DurationKey>> = [
  { value: '30', label: '30s' },
  { value: '40', label: '40s' },
  { value: '60', label: '60s' },
]

// Mantemos sincronizado com as pastas reais em data/music/ no backend:
// epic, calm, fun, mysterious, tense. Cada mood que não tem pasta dispara
// o aviso "Música indisponível" e o vídeo sai sem trilha.
type MoodKey = 'epic' | 'tense' | 'fun' | 'mysterious' | 'calm'
const MOODS: ReadonlyArray<SegmentedOption<MoodKey>> = [
  { value: 'epic', label: 'Épica' },
  { value: 'tense', label: 'Tensa' },
  { value: 'fun', label: 'Animada' },
  { value: 'mysterious', label: 'Misteriosa' },
  { value: 'calm', label: 'Calma' },
]

const CURRENT_YEAR = new Date().getFullYear()
const DEFAULT_SEASON = CURRENT_YEAR

const schema = z.object({
  season: z.number().int().min(2010).max(CURRENT_YEAR + 1),
  // Empty string = use the most recent round with ≥3 finished matches
  // (resolved server-side by football-feed). We parse to number | null
  // in formToPayload.
  round: z
    .union([z.string().regex(/^\d+$/), z.literal('')])
    .optional()
    .refine(
      (v) => v === undefined || v === '' || (Number(v) >= 1 && Number(v) <= 38),
      { message: 'Rodada deve estar entre 1 e 38 (deixe vazio para usar a atual)' },
    ),
  language: z.enum(['pt-br', 'en', 'both']),
  duration: z.number().int().min(15).max(120),
  music_mood: z.enum(['epic', 'tense', 'fun', 'mysterious', 'calm']),
  // Issue #24 Feat #4 + card #103 — picks the backend video_type slug.
  match_mode: z.enum(['top3_standings', 'next_round_preview', 'season_insight']),
  // Card #103 — recorte da temporada; só viaja quando o formato é season_insight.
  // Derivado de THEME_OPTIONS em vez de repetido: manter duas listas em
  // sincronia à mão foi exatamente o que deixou os seis temas do card #105
  // fora do formulário por dias.
  theme: z.enum(THEME_KEYS as [ThemeKey, ...ThemeKey[]]),
  // Issue #24 Feat #8 — toggle for the standings outro overlay + narrated mention.
  show_standings_outro: z.boolean(),
  llm_provider: z.enum(['auto', 'cloud', 'local']).optional(),
})

type FormValues = z.infer<typeof schema>

const FORM_DEFAULTS: FormValues = {
  season: DEFAULT_SEASON,
  round: '',
  language: 'pt-br',
  duration: 40,
  music_mood: 'epic',
  match_mode: 'top3_standings',
  theme: 'auto',
  show_standings_outro: true,
  llm_provider: 'auto',
}

function formToPayload(values: FormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    season: values.season,
    language: values.language,
    duration: values.duration,
    music_mood: values.music_mood,
    video_type: values.match_mode,
    show_standings_outro: values.show_standings_outro,
  }
  // ``round`` só faz sentido nos formatos presos à rodada — o season_insight
  // resolve a última fechada sozinho, e um round salvo no preset ficaria
  // congelado numa rodada que envelhece.
  if (values.match_mode !== 'season_insight' && values.round && values.round !== '') {
    payload.round = Number(values.round)
  }
  if (values.match_mode === 'season_insight') {
    payload.theme = values.theme === 'auto' ? '' : values.theme
  }
  return payload
}

function payloadToForm(params: Record<string, unknown>): FormValues {
  const language = params.language
  const mood = params.music_mood
  const rawSeason = params.season
  const rawRound = params.round
  const rawVideoType = params.video_type
  const matchMode: MatchModeKey =
    rawVideoType === 'next_round_preview' || rawVideoType === 'season_insight'
      ? rawVideoType
      : 'top3_standings'
  // Backend grava "" para automático; a UI representa isso como 'auto'.
  const rawTheme = params.theme
  const theme: ThemeKey =
    typeof rawTheme === 'string' && (THEME_KEYS as readonly string[]).includes(rawTheme)
      ? (rawTheme as ThemeKey)
      : 'auto'
  return {
    ...FORM_DEFAULTS,
    season:
      typeof rawSeason === 'number'
        ? rawSeason
        : typeof rawSeason === 'string' && rawSeason !== ''
        ? Number(rawSeason) || FORM_DEFAULTS.season
        : FORM_DEFAULTS.season,
    round:
      typeof rawRound === 'number'
        ? String(rawRound)
        : typeof rawRound === 'string'
        ? rawRound
        : '',
    language:
      language === 'pt-br' || language === 'en' || language === 'both' ? language : FORM_DEFAULTS.language,
    duration: typeof params.duration === 'number' ? params.duration : FORM_DEFAULTS.duration,
    music_mood:
      mood === 'epic' || mood === 'tense' || mood === 'fun' || mood === 'mysterious' || mood === 'calm'
        ? mood
        : FORM_DEFAULTS.music_mood,
    match_mode: matchMode,
    theme,
    show_standings_outro:
      typeof params.show_standings_outro === 'boolean'
        ? params.show_standings_outro
        : FORM_DEFAULTS.show_standings_outro,
  }
}

export function FootballForm() {
  const { active } = useActiveChannelFromUrl()
  const channel = active?.slug ?? ''

  const startMutation = useStartFootball()
  const [activeJobId, setActiveJobId] = useState<string | null>(null)

  // Catalog drives the QualityTierSelector — the component returns null
  // when supported_tiers doesn't include 'high', so this row auto-hides
  // if football's hooks ever get rolled back to NoopQualityHooks.
  const { data: catalog = [] } = useFactoryCatalog(channel || null)
  const footballEntry = catalog.find((c) => c.slug === 'football')

  // Quality tier lives outside react-hook-form so it stays out of the
  // PresetBar payload (a saved preset never carries the tier — it always
  // resets on visit / "Gerar outro"). Default is ``'high'`` for
  // HQ-capable factories; the effect below downgrades to ``'standard'``
  // if the catalog reports HQ isn't supported (defensive — football
  // always advertises 'high' today).
  const [qualityTier, setQualityTier] = useState<QualityTier>('high')
  useEffect(() => {
    if (footballEntry && !footballEntry.supported_tiers.includes('high')) {
      setQualityTier('standard')
    }
  }, [footballEntry])

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: FORM_DEFAULTS,
  })

  const season = form.watch('season')
  const round = form.watch('round') ?? ''
  const language = form.watch('language')
  const duration = form.watch('duration')
  const mood = form.watch('music_mood')
  const matchMode = form.watch('match_mode')
  const theme = form.watch('theme')
  const isSeasonInsight = matchMode === 'season_insight'
  const showStandingsOutro = form.watch('show_standings_outro')
  const llmProvider = form.watch('llm_provider')
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const onSubmit = form.handleSubmit((values) => {
    // ``quality_tier`` is injected here (not via PresetBar's
    // formToPayload) so it stays out of the preset's persisted JSON
    // — same pattern viral_shorts uses for decisão 57.
    const payload: FootballInput = {
      channel: channel || null,
      language: values.language,
      music_mood: values.music_mood,
      video_type: values.match_mode,
      // Card #103 — o recorte de temporada resolve a rodada sozinho (a última
      // fechada) e ignora ``round``; mandar um valor aqui só confundiria quem
      // for ler o payload no job depois.
      theme:
        values.match_mode === 'season_insight' && values.theme !== 'auto' ? values.theme : '',
      season: values.season,
      round:
        values.match_mode !== 'season_insight' && values.round ? Number(values.round) : null,
      duration: values.duration,
      quality_tier: qualityTier,
      show_standings_outro: values.show_standings_outro,
      llm_provider: values.llm_provider ?? 'auto',
    }
    startMutation.mutate(payload, {
      onSuccess: (data) => {
        setActiveJobId(data.job_id)
      },
    })
  })

  if (activeJobId) {
    return (
      <div className={styles.panelShell}>
        <JobProgressPanel jobId={activeJobId} />
        <div className={styles.panelActions}>
          <Button
            variant="secondary"
            onClick={() => {
              setActiveJobId(null)
              form.reset()
              // Fresh form resets to the factory's default tier — HQ when
              // supported, standard otherwise.
              setQualityTier(footballEntry?.supported_tiers.includes('high') ? 'high' : 'standard')
            }}
          >
            Gerar outro
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.shell}>
      <CinemaBackground />
      <div className={styles.content}>
        <header className={styles.header}>
          <div className={styles.kicker}>
            <Trophy aria-hidden /> Brasileirão · Top 3 + Tabela
          </div>
          <h1 className={styles.title}>O que você quer destacar?</h1>
          <p className={styles.subtitle}>
            {isSeasonInsight
              ? 'Um corte da temporada acumulada — sequências, corrida do título, artilharia, casa x fora. Números de todos os jogos já disputados, sem depender de rodada nova.'
              : 'Top 3 jogos com mais gols da rodada, artilheiro e tabela atualizada. Deixe a rodada em branco para usar a rodada atual.'}{' '}
            Dados via <code>football-feed</code> (UOL + ESPN).
          </p>
        </header>

        <BackToFactoryLink />

        <form className={styles.glass} onSubmit={onSubmit} noValidate>
          {startMutation.isError ? (
            <FormErrorSummary error={startMutation.error} fieldLabels={FIELD_LABELS} />
          ) : null}
          {channel ? (
            <PresetBar<FormValues>
              factory="football"
              channel={channel}
              formValues={form.watch()}
              formToPayload={formToPayload}
              payloadToForm={payloadToForm}
              onApplyPreset={(values) => {
                form.reset(values)
                // Reset HQ on preset apply: presets never carry tier
                // (decisão 57), and switching presets shouldn't silently
                // change the cost class of the next generation.
                setQualityTier('standard')
              }}
            />
          ) : null}

          <div className={styles.rows}>
            <Row icon={<CalendarDays />} label="Temporada" htmlFor="football-season">
              <input
                id="football-season"
                type="number"
                inputMode="numeric"
                min={2010}
                max={CURRENT_YEAR + 1}
                className={cn(styles.input, form.formState.errors.season && styles.inputError)}
                value={season}
                onChange={(e) => {
                  form.setValue('season', Number(e.target.value) || FORM_DEFAULTS.season, {
                    shouldValidate: true,
                  })
                }}
              />
              {form.formState.errors.season ? (
                <span className={styles.errorText}>{form.formState.errors.season.message}</span>
              ) : null}
            </Row>

            {/* Card #103 — o recorte de temporada resolve a rodada sozinho
                (a última fechada). Mostrar o campo aqui prometeria um
                controle que o backend ignora. */}
            {!isSeasonInsight ? (
              <Row icon={<Hash />} label="Rodada" htmlFor="football-round">
                <input
                  id="football-round"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={38}
                  placeholder="Atual"
                  className={cn(styles.input, form.formState.errors.round && styles.inputError)}
                  value={round}
                  onChange={(e) => {
                    form.setValue('round', e.target.value, { shouldValidate: true })
                  }}
                  aria-describedby="football-round-hint"
                />
                <span id="football-round-hint" className={styles.hint}>
                  Entre 1 e 38. Deixe em branco para usar a rodada atual.
                </span>
                {form.formState.errors.round ? (
                  <span className={styles.errorText}>{form.formState.errors.round.message}</span>
                ) : null}
              </Row>
            ) : null}

            <Row icon={<Globe />} label="Idioma">
              <Segmented<'pt-br' | 'en' | 'both'>
                options={LANGUAGES}
                value={language}
                onValueChange={(v) => {
                  form.setValue('language', v)
                }}
                aria-label="Idioma"
              />
            </Row>

            <Row icon={<Clock />} label="Duração">
              <Segmented<DurationKey>
                options={DURATIONS}
                value={String(duration) as DurationKey}
                onValueChange={(v) => {
                  form.setValue('duration', Number(v))
                }}
                aria-label="Duração alvo do vídeo"
              />
            </Row>

            <Row icon={<Music2 />} label="Trilha">
              <Segmented<MoodKey>
                options={MOODS}
                value={mood}
                onValueChange={(v) => {
                  form.setValue('music_mood', v)
                }}
                aria-label="Mood da trilha"
              />
            </Row>

            {/* Issue #24 Feat #4 + card #103 — picks the backend video_type
                slug. Backend registry today: top3_standings (last finished
                round), next_round_preview (upcoming fixtures) and
                season_insight (accumulated-season cut). */}
            <Row icon={<Swords />} label="Formato">
              <Segmented<MatchModeKey>
                options={MATCH_MODES}
                value={matchMode}
                onValueChange={(v) => {
                  form.setValue('match_mode', v)
                }}
                aria-label="Formato do vídeo"
              />
            </Row>

            {/* Card #103 — qual corte da temporada narrar. Só aparece no
                formato "Temporada"; nos outros dois o backend ignora. */}
            {isSeasonInsight ? (
              <Row icon={<TrendingUp />} label="Recorte" htmlFor="football-theme">
                <SelectRoot
                  value={theme}
                  onValueChange={(v) => {
                    form.setValue('theme', v as ThemeKey, { shouldValidate: true })
                  }}
                >
                  <SelectTrigger id="football-theme" aria-label="Recorte da temporada">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {THEME_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
                <span className={styles.hint}>
                  Números acumulados da temporada inteira — não depende de rodada nova.
                </span>
              </Row>
            ) : null}

            {/* QualityTierSelector self-hides when supported_tiers
                doesn't include 'high' (decisão 54) — so the row collapses
                cleanly if football's hooks ever roll back to Noop. */}
            {footballEntry?.supported_tiers.includes('high') ? (
              <Row icon={<Gauge />} label="Qualidade">
                <QualityTierSelector
                  value={qualityTier}
                  onValueChange={setQualityTier}
                  supportedTiers={footballEntry.supported_tiers}
                  costEstimate={footballEntry.cost_estimate}
                />
              </Row>
            ) : null}

            {/* Issue #24 Feat #8 — Ajuste fino. Collapsible section
                hosts the standings-outro toggle. Default OPEN never;
                user opens explicitly. Hidden when the format is
                next_round_preview because that fetcher emits no
                standings; season_insight does emit the top-6, so the
                toggle genuinely changes what renders there. */}
            {advancedOpen && (matchMode === 'top3_standings' || isSeasonInsight) ? (
              <Row icon={<ListOrdered />} label="Tabela no final">
                <label className={styles.toggleLabel}>
                  <input
                    type="checkbox"
                    checked={showStandingsOutro}
                    onChange={(e) => {
                      form.setValue('show_standings_outro', e.target.checked)
                    }}
                  />
                  <span>{showStandingsOutro ? 'Exibindo' : 'Oculto'}</span>
                </label>
              </Row>
            ) : null}

            {advancedOpen ? (
              <div className={styles.advanced}>
                <LlmProviderPicker
                  value={llmProvider ?? 'auto'}
                  onChange={(v) => {
                    form.setValue('llm_provider', v)
                  }}
                />
              </div>
            ) : null}
          </div>

          <footer className={styles.cta}>
            <button
              type="button"
              className={cn(styles.advToggle, advancedOpen && styles.advToggleOpen)}
              onClick={() => {
                setAdvancedOpen((o) => !o)
              }}
            >
              Ajuste fino
            </button>
            <div className={styles.ctaSpacer} />
            <GenerationTimeHint videoKind="football" />
            <button
              type="submit"
              className={styles.generateBtn}
              disabled={!channel || startMutation.isPending}
            >
              <Sparkles aria-hidden />
              {startMutation.isPending ? 'Iniciando...' : 'Gerar'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  )
}

interface RowProps {
  icon: ReactNode
  label: ReactNode
  htmlFor?: string
  children: ReactNode
}

function Row({ icon, label, htmlFor, children }: RowProps) {
  return (
    <div className={styles.row}>
      {htmlFor ? (
        <label className={styles.rowLabel} htmlFor={htmlFor}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </label>
      ) : (
        <div className={styles.rowLabel}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </div>
      )}
      <div className={styles.rowControl}>{children}</div>
    </div>
  )
}
